import { type MaintenanceWorkOrder } from '@gb-ticket/shared-types';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ApiRequestError } from '@/lib/api';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import {
  useArchiveMaintenanceWorkOrder,
  useRestoreMaintenanceWorkOrder,
} from '@/products/maintenance/features/work-orders/use-maintenance-work-orders';

type ArchiveOrder = Pick<MaintenanceWorkOrder, 'id' | 'orderNumber' | 'archivedAt'>;

export function MaintenanceWorkOrderArchivedBadge({
  order,
}: {
  order: Pick<MaintenanceWorkOrder, 'archivedAt'>;
}) {
  if (order.archivedAt == null) {
    return null;
  }
  return (
    <Badge variant="secondary" className="shrink-0 font-normal">
      Archiviert
    </Badge>
  );
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiRequestError ? error.message : fallback;
}

export function MaintenanceWorkOrderArchiveButton({ order }: { order: ArchiveOrder }) {
  const copy = useVoiceCopy();
  const archive = useArchiveMaintenanceWorkOrder();
  const restore = useRestoreMaintenanceWorkOrder();
  const [open, setOpen] = useState(false);
  const pending = archive.isPending || restore.isPending;
  const archived = order.archivedAt != null;

  async function confirm() {
    try {
      if (archived) {
        await restore.mutateAsync(order.id);
        toast.success('Auftrag wieder in der Liste.');
      } else {
        await archive.mutateAsync(order.id);
        toast.success('Auftrag archiviert.');
      }
      setOpen(false);
    } catch (error: unknown) {
      toast.error(
        errorMessage(
          error,
          archived
            ? 'Der Auftrag konnte nicht wiederhergestellt werden.'
            : 'Der Auftrag konnte nicht archiviert werden.',
        ),
      );
    }
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="min-h-11"
        size="sm"
        onClick={() => setOpen(true)}
      >
        {archived ? 'Wiederherstellen' : 'Archivieren'}
      </Button>
      <AlertDialog
        open={open}
        onOpenChange={(next) => {
          if (!next && !pending) {
            setOpen(false);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {archived
                ? `Auftrag ${order.orderNumber} wiederherstellen?`
                : `Auftrag ${order.orderNumber} archivieren?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {archived
                ? 'Der Auftrag erscheint wieder unter Meine Aufträge.'
                : copy('maintenance.work-order.archive.one')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={pending}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              disabled={pending}
              onClick={(event) => {
                event.preventDefault();
                void confirm();
              }}
            >
              {archived ? 'Wiederherstellen' : 'Archivieren'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function MaintenanceWorkOrderArchiveBar({
  orders,
  selectedIds,
  onClear,
}: {
  orders: readonly ArchiveOrder[];
  selectedIds: ReadonlySet<string>;
  onClear: () => void;
}) {
  const copy = useVoiceCopy();
  const selected = orders.filter((order) => selectedIds.has(order.id));
  const toArchive = selected.filter((order) => order.archivedAt == null);
  const toRestore = selected.filter((order) => order.archivedAt != null);
  const archive = useArchiveMaintenanceWorkOrder();
  const restore = useRestoreMaintenanceWorkOrder();
  const [mode, setMode] = useState<'archive' | 'restore' | null>(null);
  const [pending, setPending] = useState(false);

  if (selected.length === 0) {
    return null;
  }

  async function run(rows: readonly ArchiveOrder[], kind: 'archive' | 'restore') {
    if (rows.length === 0 || pending) {
      return;
    }
    setPending(true);
    const failures: string[] = [];
    for (const row of rows) {
      try {
        if (kind === 'archive') {
          await archive.mutateAsync(row.id);
        } else {
          await restore.mutateAsync(row.id);
        }
      } catch (error: unknown) {
        failures.push(
          `${row.orderNumber}: ${errorMessage(error, kind === 'archive' ? 'Archivieren fehlgeschlagen' : 'Wiederherstellen fehlgeschlagen')}`,
        );
      }
    }
    setPending(false);
    setMode(null);
    onClear();
    if (failures.length > 0) {
      toast.error(failures.join(' '));
      return;
    }
    toast.success(
      kind === 'archive'
        ? rows.length === 1
          ? 'Auftrag archiviert.'
          : `${rows.length} Aufträge archiviert.`
        : rows.length === 1
          ? 'Auftrag wieder in der Liste.'
          : `${rows.length} Aufträge wieder in der Liste.`,
    );
  }

  const confirming = mode === 'archive' ? toArchive : mode === 'restore' ? toRestore : [];

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-muted-foreground">{selected.length} ausgewählt</p>
        {toArchive.length > 0 ? (
          <Button
            type="button"
            variant="outline"
            className="min-h-11"
            onClick={() => setMode('archive')}
          >
            Archivieren
          </Button>
        ) : null}
        {toRestore.length > 0 ? (
          <Button
            type="button"
            variant="outline"
            className="min-h-11"
            onClick={() => setMode('restore')}
          >
            Wiederherstellen
          </Button>
        ) : null}
        <Button type="button" variant="ghost" className="min-h-11" onClick={onClear}>
          Auswahl aufheben
        </Button>
      </div>
      <AlertDialog
        open={mode != null}
        onOpenChange={(open) => {
          if (!open && !pending) {
            setMode(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {mode === 'restore'
                ? confirming.length === 1
                  ? `Auftrag ${confirming[0]?.orderNumber ?? ''} wiederherstellen?`
                  : `${confirming.length} Aufträge wiederherstellen?`
                : confirming.length === 1
                  ? `Auftrag ${confirming[0]?.orderNumber ?? ''} archivieren?`
                  : `${confirming.length} Aufträge archivieren?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {mode === 'restore'
                ? 'Die Aufträge erscheinen wieder unter Meine Aufträge.'
                : confirming.length === 1
                  ? copy('maintenance.work-order.archive.one')
                  : copy('maintenance.work-order.archive.many')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {confirming.length > 1 ? (
            <ul className="list-disc pl-5 text-sm text-muted-foreground">
              {confirming.map((row) => (
                <li key={row.id}>{row.orderNumber}</li>
              ))}
            </ul>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={pending}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              disabled={pending || mode == null}
              onClick={(event) => {
                event.preventDefault();
                if (mode) {
                  void run(confirming, mode);
                }
              }}
            >
              {mode === 'restore' ? 'Wiederherstellen' : 'Archivieren'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
